import { AxiosError } from 'axios';
import { useContext } from 'react';

import AppContext from '@/contexts/appContext';
import type { BillingReportFormPayload, BillingReportResponse } from '@/types/billingReport';
import type RepositoryResponse from '@/types/repositoryResponse';
import ajax from '@/utils/ajax';
import logAjaxError from '@/utils/logAjaxError';

export const billingReportRepository = {
  async getBillingReport({
    host,
    reportId
  }: {
    host: string;
    reportId: string;
  }): Promise<RepositoryResponse<BillingReportResponse>> {
    try {
      const path = `/api/billing/billing_reports/${reportId}/`;

      const response = await ajax.get({ url: `${host}${path}` });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);

      return {
        errorMessage: `Failed to get billing report: ${(error as AxiosError).message}`,
        error: error as AxiosError,
        isError: true
      };
    }
  },

  async updateBillingReport({
    host,
    report
  }: {
    host: string;
    report: BillingReportFormPayload;
  }): Promise<RepositoryResponse<BillingReportResponse>> {
    try {
      const path = `/api/billing/billing_reports/${report.id}/`;

      const response = await ajax.put({ url: `${host}${path}`, data: report });

      return {
        data: response.data as unknown as BillingReportResponse,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);

      return {
        errorMessage: `Failed to update billing report: ${(error as AxiosError).message}`,
        error: error as AxiosError,
        isError: true
      };
    }
  },

  async createBillingReport({
    host,
    report
  }: {
    host: string;
    report: BillingReportFormPayload;
  }): Promise<RepositoryResponse<BillingReportResponse>> {
    try {
      const path = `/api/billing/billing_reports/`;

      const response = await ajax.post({ url: `${host}${path}`, data: report });

      return {
        data: response.data as unknown as BillingReportResponse,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);

      return {
        errorMessage: `Failed to create billing report: ${(error as AxiosError).message}`,
        error: error as AxiosError,
        isError: true
      };
    }
  },

  async deleteBillingReport({
    host,
    reportId
  }: {
    host: string;
    reportId: string;
  }): Promise<RepositoryResponse<null>> {
    try {
      const path = `/api/billing/billing_reports/${reportId}/`;

      await ajax.delete({ url: `${host}${path}` });

      return { data: null, isError: false };
    } catch (error) {
      logAjaxError(error);

      return {
        errorMessage: `Failed to delete billing report: ${(error as AxiosError).message}`,
        error: error as AxiosError,
        isError: true
      };
    }
  }
};

const BillingReportRepository = () => {
  const { host } = useContext(AppContext);

  return {
    async createBillingReport(
      report: BillingReportFormPayload
    ): Promise<RepositoryResponse<BillingReportResponse>> {
      return billingReportRepository.createBillingReport({ host, report });
    },
    async getBillingReport(reportId: string): Promise<RepositoryResponse<BillingReportResponse>> {
      return billingReportRepository.getBillingReport({ host, reportId });
    },
    async updateBillingReport(
      report: BillingReportFormPayload
    ): Promise<RepositoryResponse<BillingReportResponse>> {
      return billingReportRepository.updateBillingReport({ host, report });
    },
    async deleteBillingReport(reportId: string): Promise<RepositoryResponse<null>> {
      return billingReportRepository.deleteBillingReport({ host, reportId });
    }
  };
};

export default BillingReportRepository;
